import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';
import { sendCongressConfirmationEmail } from '../lib/email';

const REGISTRATION_ENABLED = true;

interface FormData {
  first_name: string;
  last_name: string;
  institution: string;
  profession: string;
  license_type: string;
  license_number: string;
  email: string;
  phone: string;
  payment_type: string;
  sponsor_name: string;
  needs_accommodation: boolean;
  accommodation_dates: string[];
  room_type: string;
  roommate_name: string;
  needs_transport: boolean;
}

export function RegistrationPage() {
  if (!REGISTRATION_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl w-full bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 text-center">
            <h1 className="text-3xl font-bold">Prijave privremeno zatvorene</h1>
            <p className="text-blue-100 mt-3">
              Prijave za kongres trenutno nisu dostupne. Molimo pratite službene kanale za nove informacije.
            </p>
          </div>
          <div className="p-8 space-y-6 text-center">
            <p className="text-gray-700">
              Pripremamo novi ciklus prijava. Ako imate pitanja, kontaktirajte nas putem
              <a href="mailto:info@studio-arena.hr" className="text-blue-600 font-semibold ml-1 hover:underline">
                info@studio-arena.hr
              </a>
              .
            </p>
            <Link 
              to="/kongres"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Povratak na stranicu kongresa
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    institution: '',
    profession: '',
    license_type: '',
    license_number: '',
    email: '',
    phone: '',
    payment_type: '',
    sponsor_name: '',
    needs_accommodation: false,
    accommodation_dates: [],
    room_type: '',
    roommate_name: '',
    needs_transport: false
  });

  // Reset license fields when profession changes
  useEffect(() => {
    if (formData.profession) {
      // Set license type automatically based on profession
      let licenseType = '';
      
      if (formData.profession === 'nurse') {
        licenseType = 'hkms';
      } else if (formData.profession === 'doctor') {
        licenseType = 'hlk';
      }
      
      setFormData(prev => ({
        ...prev,
        license_type: licenseType,
        license_number: '' // Reset license number
      }));
    }
  }, [formData.profession]);

  // Check if license fields are required based on profession
  const isLicenseRequired = formData.profession === 'nurse' || formData.profession === 'doctor';
  
  // Get available license types based on profession
  const getLicenseOptions = () => {
    switch (formData.profession) {
      case 'nurse':
        return [{ value: 'hkms', label: 'HKMS' }];
      case 'doctor':
        return [{ value: 'hlk', label: 'HLK' }];
      default:
        return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Čisti podatke prije slanja
      const cleanedFormData = {
        ...formData,
        // Ako ne treba smještaj, postavi accommodation polja na null
        accommodation_dates: formData.needs_accommodation && formData.accommodation_dates.length > 0 
          ? formData.accommodation_dates 
          : null,
        room_type: formData.needs_accommodation && formData.room_type 
          ? formData.room_type 
          : null,
        roommate_name: formData.needs_accommodation && formData.roommate_name 
          ? formData.roommate_name 
          : null,
        // Ako nema sponzora, postavi sponsor_name na null
        sponsor_name: formData.payment_type === 'sponsor' && formData.sponsor_name 
          ? formData.sponsor_name 
          : null,
        // Ako nema licence, postavi licence polja na null
        license_type: formData.license_type || null,
        license_number: formData.license_number || null
      };

      console.log('Šaljem podatke:', cleanedFormData);

      const { error } = await supabase
        .from('registrations')
        .insert([cleanedFormData]);

      if (error) throw error;

      toast.success('Uspješno ste se prijavili na kongres!');

      // Pokušaj poslati email potvrde (ne blokira korisnika)
      sendCongressConfirmationEmail({
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email,
        needsAccommodation: formData.needs_accommodation,
        needsTransport: formData.needs_transport,
        institution: formData.institution,
        profession: formData.profession,
        licenseType: formData.license_type,
        licenseNumber: formData.license_number,
        phone: formData.phone,
        paymentType: formData.payment_type,
        sponsorName: formData.sponsor_name,
        accommodationDates: formData.accommodation_dates,
        roomType: formData.room_type,
        roommateName: formData.roommate_name
      }).catch((err) => {
        console.error('Slanje email potvrde nije uspjelo:', err);
      });

      // Spremi podatke za prikaz potvrde
      setSubmittedData({ ...formData });
      setSubmissionSuccessful(true);
    } catch (error: any) {
      console.error('Detaljne informacije o grešci:', {
        error: error,
        message: error?.message,
        code: error?.code,
        hint: error?.hint,
        details: error?.details
      });
      
      // Handle specific error types
      if (error?.code === '23505') {
        // Unique constraint violation
        toast.error('Već ste registrirani s ovim podacima. Molimo provjerite email adresu ili broj licence.');
      } else if (error?.code === '23514') {
        // Check constraint violation
        toast.error('Podaci nisu ispravno uneseni. Molimo provjerite sva polja.');
      } else if (error?.message?.includes('409') || error?.status === 409) {
        // 409 Conflict error
        toast.error('Već ste registrirani. Molimo provjerite vaše podatke.');
      } else if (error?.status === 400 || error?.code === '400') {
        // 400 Bad Request error
        toast.error('Podaci nisu valjani. Molimo provjerite da su sva obavezna polja ispravno popunjena.');
        console.log('400 error details:', error);
      } else if (error?.code === '42P01') {
        // Table doesn't exist
        toast.error('Tablica registrations ne postoji u bazi podataka. Molimo kontaktirajte administratora.');
        console.log('HITNO: Potrebno je kreirati tablicu registrations u Supabase!');
      } else {
        // Generic error with more details
        toast.error(`Greška: ${error?.message || 'Nepoznata greška'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? e.target.checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({
      ...prev,
      accommodation_dates: prev.accommodation_dates.includes(date)
        ? prev.accommodation_dates.filter(d => d !== date)
        : [...prev.accommodation_dates, date]
    }));
  };

  // Komponenta za prikaz potvrde prijave
  const RegistrationConfirmation = () => {
    if (!submittedData) return null;
    
    // Pomoćne funkcije za prikazivanje podataka
    const getProfessionLabel = (value: string) => {
      switch(value) {
        case 'nurse': return 'Medicinska sestra/tehničar';
        case 'doctor': return 'Liječnik';
        case 'other': return 'Ostalo';
        default: return '';
      }
    };
    
    const getPaymentTypeLabel = (value: string) => {
      switch(value) {
        case 'personal': return 'Osobno';
        case 'institution': return 'Zdravstvena ustanova';
        case 'sponsor': return 'Sponzor';
        default: return '';
      }
    };
    
    const getRoomTypeLabel = (value: string) => {
      switch(value) {
        case 'single': return 'Jednokrevetna soba';
        case 'double': return 'Dvokrevetna soba';
        default: return '';
      }
    };
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-center">
            Uspješna prijava na kongres!
          </h1>
          <p className="text-green-100 text-center mt-2">
            Hvala vam na vašoj prijavi
          </p>
        </div>

        <div className="p-8">
          <div className="mb-8 bg-green-50 p-6 rounded-lg border border-green-200">
            <p className="text-gray-800">
              Vaša prijava je uspješno zaprimljena. Svi podaci su pohranjeni u našoj bazi podataka.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Pregled prijavljenih podataka</h2>
          
          <div className="space-y-6">
            {/* Osobni podaci */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Osobni podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Ime</p>
                    <p className="text-gray-900 font-medium">{submittedData.first_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Prezime</p>
                    <p className="text-gray-900 font-medium">{submittedData.last_name}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profesionalni podaci */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Profesionalni podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Institucija</p>
                    <p className="text-gray-900 font-medium">{submittedData.institution}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Profesija</p>
                    <p className="text-gray-900 font-medium">{getProfessionLabel(submittedData.profession)}</p>
                  </div>
                  {submittedData.license_type && (
                    <div>
                      <p className="text-sm text-gray-500">Vrsta licence</p>
                      <p className="text-gray-900 font-medium">{submittedData.license_type}</p>
                    </div>
                  )}
                  {submittedData.license_number && (
                    <div>
                      <p className="text-sm text-gray-500">Broj licence</p>
                      <p className="text-gray-900 font-medium">{submittedData.license_number}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Kontakt podaci */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Kontakt podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900 font-medium">{submittedData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="text-gray-900 font-medium">{submittedData.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Način plaćanja */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Način plaćanja</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 font-medium">
                  {getPaymentTypeLabel(submittedData.payment_type)}
                </p>
                {submittedData.payment_type === 'sponsor' && submittedData.sponsor_name && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Ime sponzora</p>
                    <p className="text-gray-900 font-medium">{submittedData.sponsor_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Smještaj i prijevoz */}
            {(submittedData.needs_accommodation || submittedData.needs_transport) && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Smještaj i prijevoz</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {submittedData.needs_accommodation && (
                    <div className="mb-4">
                      <p className="text-gray-900 font-medium mb-2">Potreban smještaj: Da</p>
                      
                      {submittedData.accommodation_dates.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">Datumi</p>
                          <ul className="list-disc pl-5 text-gray-900">
                            {submittedData.accommodation_dates.map((date, index) => (
                              <li key={index}>{date}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {submittedData.room_type && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-500">Vrsta sobe</p>
                          <p className="text-gray-900">{getRoomTypeLabel(submittedData.room_type)}</p>
                          
                          {submittedData.room_type === 'double' && submittedData.roommate_name && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">Osoba za dijeljenje sobe</p>
                              <p className="text-gray-900">{submittedData.roommate_name}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {submittedData.needs_transport && (
                    <div>
                      <p className="text-gray-900 font-medium">Potreban prijevoz: Da</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                setSubmissionSuccessful(false);
                // Resetiraj formu na početne vrijednosti
                setFormData({
                  first_name: '',
                  last_name: '',
                  institution: '',
                  profession: '',
                  license_type: '',
                  license_number: '',
                  email: '',
                  phone: '',
                  payment_type: '',
                  sponsor_name: '',
                  needs_accommodation: false,
                  accommodation_dates: [],
                  room_type: '',
                  roommate_name: '',
                  needs_transport: false
                });
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Napravi novu prijavu
            </button>
            <button
              onClick={() => navigate('/kongres')}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Povratak na početnu stranicu
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4"
      >
        {submissionSuccessful ? (
          <RegistrationConfirmation />
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
              <h1 className="text-3xl font-bold text-center">Prijava za kongres</h1>
              <p className="text-blue-100 text-center mt-2">
                Molimo ispunite sve potrebne podatke za prijavu
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 md:p-8 space-y-6 md:space-y-8">
              {/* Personal Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4 md:space-y-6"
              >
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Osobni podaci</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ime
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prezime
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Professional Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-4 md:space-y-6"
              >
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Profesionalni podaci</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institucija
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="institution"
                      required
                      value={formData.institution}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profesija
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                      {[
                        { value: 'nurse', label: 'Medicinska sestra/tehničar' },
                        { value: 'doctor', label: 'Liječnik' },
                        { value: 'other', label: 'Ostalo' }
                      ].map((option) => (
                        <div 
                          key={option.value}
                          className={`
                            border rounded-lg p-4 text-center cursor-pointer transition-all duration-200
                            ${formData.profession === option.value 
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                              : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            }
                          `}
                          onClick={() => setFormData(prev => ({ ...prev, profession: option.value }))}
                        >
                          <input 
                            type="radio"
                            name="profession"
                            value={option.value}
                            checked={formData.profession === option.value}
                            onChange={handleInputChange}
                            className="sr-only"
                            required={formData.profession === ''}
                          />
                          <span className="text-sm font-medium">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {formData.profession && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="grid md:grid-cols-2 gap-6"
                    >
                      {/* Conditional License Type Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Vrsta licence
                          {isLicenseRequired ? (
                            <span className="text-red-500 ml-1">*</span>
                          ) : (
                            <span className="text-gray-500 ml-2 text-xs">(neobavezno)</span>
                          )}
                        </label>
                        
                        {/* For nurse or doctor: show selected license type */}
                        {(formData.profession === 'nurse' || formData.profession === 'doctor') && (
                          <div className="border rounded-lg p-3 bg-blue-600 text-white border-blue-600 shadow-md">
                            <span className="text-sm font-medium">
                              {formData.profession === 'nurse' ? 'HKMS' : 'HLK'}
                            </span>
                          </div>
                        )}
                        
                        {/* For other: text input field */}
                        {formData.profession === 'other' && (
                          <input
                            type="text"
                            name="license_type"
                            placeholder="Unesite vrstu licence"
                            value={formData.license_type}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                      </div>
                      
                      {/* License Number field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Broj licence
                          {isLicenseRequired ? (
                            <span className="text-red-500 ml-1">*</span>
                          ) : (
                            <span className="text-gray-500 ml-2 text-xs">(neobavezno)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          name="license_number"
                          required={isLicenseRequired}
                          value={formData.license_number}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-4 md:space-y-6"
              >
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Kontakt podaci</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Payment Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-4 md:space-y-6"
              >
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Način plaćanja
                  <span className="text-red-500 ml-1">*</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'personal', label: 'Osobno' },
                    { value: 'institution', label: 'Zdravstvena ustanova' },
                    { value: 'sponsor', label: 'Sponzor' }
                  ].map((option) => (
                    <div 
                      key={option.value}
                      className={`
                        border rounded-lg p-4 text-center cursor-pointer transition-all duration-200
                        ${formData.payment_type === option.value 
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                      onClick={() => setFormData(prev => ({ ...prev, payment_type: option.value }))}
                    >
                      <input 
                        type="radio"
                        name="payment_type"
                        value={option.value}
                        checked={formData.payment_type === option.value}
                        onChange={handleInputChange}
                        className="sr-only"
                        required={formData.payment_type === ''}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                  ))}
                </div>
                {formData.payment_type === 'sponsor' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3"
                  >
                    <input
                      type="text"
                      name="sponsor_name"
                      placeholder="Naziv sponzora"
                      value={formData.sponsor_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </motion.div>
                )}
              </motion.div>

              {/* Accommodation and Transport */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-4 md:space-y-6"
              >
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">Smještaj i prijevoz</h2>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <div 
                      className={`
                        flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200
                        ${formData.needs_accommodation
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                      onClick={() => setFormData(prev => ({ ...prev, needs_accommodation: !prev.needs_accommodation }))}
                    >
                      <div className={`
                        w-5 h-5 flex items-center justify-center rounded border mr-3 transition-colors
                        ${formData.needs_accommodation
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-400'
                        }
                      `}>
                        {formData.needs_accommodation && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        name="needs_accommodation"
                        checked={formData.needs_accommodation}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <label className="text-gray-700 font-medium">Potreban mi je smještaj</label>
                    </div>
                    
                    {formData.needs_accommodation && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 p-4 md:p-5 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <p className="text-sm font-medium text-blue-800 mb-3">Odaberite datume:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                          {['1.-2.10.2026.', '2.-3.10.2026.', '3.-4.10.2026.'].map(date => (
                            <div 
                              key={date}
                              className={`
                                flex items-center p-2 md:p-3 border rounded-lg cursor-pointer transition-all duration-200
                                ${formData.accommodation_dates.includes(date)
                                  ? 'bg-white border-blue-400 shadow-sm'
                                  : 'bg-white border-gray-200 hover:border-blue-300'
                                }
                              `}
                              onClick={() => handleDateChange(date)}
                            >
                              <div className={`
                                w-5 h-5 flex items-center justify-center rounded border mr-2 md:mr-3 transition-colors
                                ${formData.accommodation_dates.includes(date)
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'border-gray-400'
                                }
                              `}>
                                {formData.accommodation_dates.includes(date) && (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.accommodation_dates.includes(date)}
                                onChange={() => handleDateChange(date)}
                                className="sr-only"
                              />
                              <label className="text-sm md:text-base text-gray-700">{date}</label>
                            </div>
                          ))}
                        </div>

                        {/* Vrsta sobe */}
                        {formData.accommodation_dates.length > 0 && (
                          <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-4"
                          >
                            <p className="text-sm font-medium text-blue-800 mb-3">Vrsta sobe:</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                              {[
                                { value: 'single', label: 'Jednokrevetna soba' },
                                { value: 'double', label: 'Dvokrevetna soba' }
                              ].map((option) => (
                                <div 
                                  key={option.value}
                                  className={`
                                    flex items-center p-2 md:p-3 border rounded-lg cursor-pointer transition-all duration-200
                                    ${formData.room_type === option.value
                                      ? 'bg-white border-blue-400 shadow-sm'
                                      : 'bg-white border-gray-200 hover:border-blue-300'
                                    }
                                  `}
                                  onClick={() => setFormData(prev => ({ ...prev, room_type: option.value }))}
                                >
                                  <div className={`
                                    w-5 h-5 flex items-center justify-center rounded-full border mr-2 md:mr-3 transition-colors
                                    ${formData.room_type === option.value
                                      ? 'bg-blue-600 border-blue-600'
                                      : 'border-gray-400'
                                    }
                                  `}>
                                    {formData.room_type === option.value && (
                                      <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                                    )}
                                  </div>
                                  <input 
                                    type="radio"
                                    name="room_type"
                                    value={option.value}
                                    checked={formData.room_type === option.value}
                                    onChange={handleInputChange}
                                    className="sr-only"
                                  />
                                  <label className="text-sm md:text-base text-gray-700">{option.label}</label>
                                </div>
                              ))}
                            </div>

                            {/* Ime osobe za dvokrevetnu sobu */}
                            {formData.room_type === 'double' && (
                              <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="mt-4"
                              >
                                <label className="block text-sm font-medium text-blue-800 mb-2">
                                  S kim želite dijeliti sobu:
                                </label>
                                <input
                                  type="text"
                                  name="roommate_name"
                                  placeholder="Ime i prezime osobe"
                                  value={formData.roommate_name}
                                  onChange={handleInputChange}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                />
                              </motion.div>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </div>
                  
                  <div 
                    className={`
                      flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200
                      ${formData.needs_transport
                        ? 'bg-blue-50 border-blue-300 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }
                    `}
                    onClick={() => setFormData(prev => ({ ...prev, needs_transport: !prev.needs_transport }))}
                  >
                    <div className={`
                      w-5 h-5 flex items-center justify-center rounded border mr-3 transition-colors
                      ${formData.needs_transport
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-400'
                      }
                    `}>
                      {formData.needs_transport && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      name="needs_transport"
                      checked={formData.needs_transport}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <label className="text-gray-700 font-medium">Potreban mi je prijevoz</label>
                  </div>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="pt-6"
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-8 text-lg font-bold text-white bg-blue-600 rounded-full 
                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                    transition-colors duration-200`}
                >
                  {isSubmitting ? 'Slanje prijave...' : 'Pošalji prijavu'}
                </button>
              </motion.div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
} 